import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { lazyGetStoreState as lazyGetTruTimeStoreState, State } from 'owa-truetime';
import getSelectedAccountProvider from '../selectors/getSelectedAccountProvider';
import getTrueTimeUpdateFailedAccount from '../selectors/getTrueTimeUpdateFailedAccount';
import getAccountsEditorState from '../selectors/getAccountsEditorState';
import {
    accountStatusError,
    trueTimeError,
    trueTimeUpdateErrorOnExistingAccount,
    trueTimeUpdateErrorDuringAdd,
    trueTimeUpdateErrorDuringRemove,
    accountRemoveError,
    accountReconnectError,
    reconnectAccount,
    removeAccountHotReload,
    accountIsBeingAddedLabel,
    accountAddFailedLabel,
    accountAddFailedAadAuth,
    getAccountsBootFailed,
} from './NotificationContent.locstring.json';
import { removeAccount } from './ConnectedAccount.locstring.json';
import loc from 'owa-localize';
import {
    OWAConnectedAccount,
    OWAConnectedAccountState,
    getAccountsStoreState,
    hasConnectedAccounts,
    AccountsStoreState,
} from 'owa-accounts-store';
import { AccountsEditorState } from '../store/schema/AccountsEditorState';
import { MessageBarType, MessageBar } from '@fluentui/react/lib/MessageBar';
import { Link } from '@fluentui/react/lib/Link';
import { Label } from '@fluentui/react/lib/Label';
import { startAddingAccount, startRemovingAccount } from '../actions/internalActions';
import getAccountPendingTrueTimeUpdate from '../selectors/getAccountPendingTrueTimeUpdate';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { isFeatureEnabled } from 'owa-feature-flags';
import { AddFailedFeedbackRibbon, TrueTimeDisabledFeedbackRibbon } from 'owa-accounts-feedback';

const styles = require('./NotificationContent.scss');

export interface NotificationContentProps {
    connectedAccounts: OWAConnectedAccount[];
    mergeViewEnabledAccount: string;
}

export default observer(function NotificationContent(props: NotificationContentProps) {
    const { type, content } = getNotificationContent(
        props.connectedAccounts,
        props.mergeViewEnabledAccount
    );

    if (content) {
        return <MessageBar messageBarType={type}>{content}</MessageBar>;
    }
    return null;
});

function getNotificationContent(
    connectedAccounts: OWAConnectedAccount[],
    mergeViewEnabledAccount: string
): { type: MessageBarType; content: JSX.Element } {
    // always use the first element while we only support one connected account
    const defaultConnectedAccount = connectedAccounts[0];
    const getTrueTimeStoreState = lazyGetTruTimeStoreState.tryImportForRender();
    const trueTimeState = getTrueTimeStoreState ? getTrueTimeStoreState() : undefined;
    const accountsEditorState = getAccountsEditorState();

    if (
        getAccountsStoreState() == AccountsStoreState.AccountsLoadError &&
        !hasConnectedAccounts()
    ) {
        // We may have just removed a connected account. In that case, we still need to
        // display the hot reload message for the account that was just removed.
        if (accountsEditorState == AccountsEditorState.Removed) {
            return {
                type: MessageBarType.warning,
                content: <Link onClick={reloadPage}>{loc(removeAccountHotReload)}</Link>,
            };
        } else {
            return {
                type: MessageBarType.error,
                content: <>{loc(getAccountsBootFailed)}</>,
            };
        }
    }

    switch (accountsEditorState) {
        case AccountsEditorState.Added:
            if (defaultConnectedAccount?.accountState != OWAConnectedAccountState.Valid) {
                return {
                    type: MessageBarType.severeWarning,
                    content: (
                        <>
                            {renderRemoveOrReconnectContent(
                                defaultConnectedAccount,
                                mergeViewEnabledAccount
                            )}
                        </>
                    ),
                };
            }
            if (trueTimeState == State.Error) {
                return {
                    type: MessageBarType.error,
                    content: <>{loc(trueTimeError)}</>,
                };
            }
            if (getTrueTimeUpdateFailedAccount() == defaultConnectedAccount?.userIdentity) {
                return {
                    type: MessageBarType.error,
                    content: <>{loc(trueTimeUpdateErrorOnExistingAccount)}</>,
                };
            }
            break;
        case AccountsEditorState.Adding:
            return {
                type: MessageBarType.info,
                content: <>{loc(accountIsBeingAddedLabel)}</>,
            };
            break;
        case AccountsEditorState.AddFailedUserCancelled:
            // if flight is enabled to go straight to a feedback dialog after user cancels, no need for notification content
            if (isFeatureEnabled('cal-multiAccounts-feedbackDialog')) {
                return {
                    type: null,
                    content: null,
                };
            } else if (
                isFeatureEnabled('cal-multiAccounts-feedbackDialog2') ||
                isFeatureEnabled('cal-multiAccounts-feedbackRibbon')
            ) {
                return {
                    type: MessageBarType.info,
                    content: <AddFailedFeedbackRibbon />,
                };
            } else {
                return {
                    type: MessageBarType.error,
                    content: <>{loc(accountAddFailedLabel)}</>,
                };
            }
            break;
        case AccountsEditorState.AddFailedAadAuth:
            return {
                type: MessageBarType.error,
                content: <>{loc(accountAddFailedAadAuth)}</>,
            };
            break;
        case AccountsEditorState.AddFailedUnknown:
            if (
                getSelectedAccountProvider() == defaultConnectedAccount?.accountProviderType &&
                getTrueTimeUpdateFailedAccount() == defaultConnectedAccount?.userIdentity
            ) {
                return {
                    type: MessageBarType.error,
                    content: <>{loc(trueTimeUpdateErrorDuringAdd)}</>,
                };
            } else {
                return {
                    type: MessageBarType.error,
                    content: <>{loc(accountAddFailedLabel)}</>,
                };
            }
            break;
        case AccountsEditorState.RemoveFailed:
            if (getSelectedAccountProvider() == defaultConnectedAccount?.accountProviderType) {
                if (getTrueTimeUpdateFailedAccount() == defaultConnectedAccount?.userIdentity) {
                    return {
                        type: MessageBarType.error,
                        content: <>{loc(trueTimeUpdateErrorDuringRemove)}</>,
                    };
                } else {
                    return {
                        type: MessageBarType.error,
                        content: <>{loc(accountRemoveError)}</>,
                    };
                }
            }
            break;
        case AccountsEditorState.ReconnectFailed:
            if (getSelectedAccountProvider() == defaultConnectedAccount?.accountProviderType) {
                return {
                    type: MessageBarType.error,
                    content: <>{loc(accountReconnectError)}</>,
                };
            }
            break;
        case AccountsEditorState.Removed:
            return {
                type: MessageBarType.warning,
                content: <Link onClick={reloadPage}>{loc(removeAccountHotReload)}</Link>,
            };
        case AccountsEditorState.TrueTimeDisabled:
            if (isFeatureEnabled('cal-trueTime-disable-feedback')) {
                return {
                    type: MessageBarType.info,
                    content: <TrueTimeDisabledFeedbackRibbon />,
                };
            }
            break;
    }
    if (
        defaultConnectedAccount &&
        defaultConnectedAccount?.accountState != OWAConnectedAccountState.Valid
    ) {
        return {
            type: MessageBarType.severeWarning,
            content: (
                <>
                    {renderRemoveOrReconnectContent(
                        defaultConnectedAccount,
                        mergeViewEnabledAccount
                    )}
                </>
            ),
        };
    } else {
        return {
            type: null,
            content: null,
        };
    }
}

function reloadPage() {
    location.reload();
}

function renderRemoveOrReconnectContent(
    connectedAccount: OWAConnectedAccount,
    mergeViewEnabledAccount: string
): JSX.Element {
    const reconnectButtonOnClick = () => {
        startAddingAccount(connectedAccount.accountProviderType, true /*reconnect flow*/);
    };
    const removeButtonOnClick = () => {
        startRemovingAccount(connectedAccount, mergeViewEnabledAccount);
    };
    const isUpdatePending = getAccountPendingTrueTimeUpdate() == connectedAccount?.userIdentity;

    return (
        <>
            <Label className={styles.errorLabel}>{loc(accountStatusError)}</Label>
            <PrimaryButton
                className={styles.reconnectButton}
                disabled={isUpdatePending}
                text={loc(reconnectAccount)}
                onClick={reconnectButtonOnClick}
                styles={{ root: { float: 'right' } }}
            />
            <DefaultButton
                className={styles.removeButton}
                disabled={isUpdatePending}
                text={loc(removeAccount)}
                onClick={removeButtonOnClick}
                styles={{ root: { float: 'right' } }}
            />
        </>
    );
}
